import type { Period, RestrictionAction } from 'lumen/models';
import {
  CHANNEL_PRICING_HOTEL_RESTRICTION_TYPES,
  RESTRICTION_MINOR_CHANNEL_ID,
  RESTRICTION_MODEL_CONTEXTS,
} from 'lumen/utils/constants/channel-pricing-constants';

/**
 * Filters restriction actions that are at the CHANNEL_LEVEL context.
 *
 * Channel-level restrictions apply to specific OTA channels (e.g., Booking.com, Expedia).
 * We only include actions that have a valid createdAt timestamp.
 *
 * @param restrictionActions - Array of restriction actions to filter
 * @returns Array of channel-level restriction actions with valid timestamps
 */

export function filterChannelLevelRestrictions(
  restrictionActions: RestrictionAction[],
): RestrictionAction[] {
  return restrictionActions.filter((action: RestrictionAction) => {
    return (
      action.context === RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL &&
      action.createdAt
    );
  });
}

/**
 * Filters restriction actions for Minor Channels.
 *
 * Minor channels are sub-channels within major OTAs. They have:
 * - CHANNEL_LEVEL context (same as regular channels)
 * - No otaId (null)
 * - A valid channelId
 * - A valid createdAt timestamp
 *
 * SMART DISTRIBUTION TODO: Discuss with BE a solution to make the logical criteria solid
 *
 * @param restrictionActions - Array of restriction actions to filter
 * @returns Array of minor channel restriction actions
 */
export function filterMinorChannelLevelRestrictions(
  restrictionActions: RestrictionAction[],
): RestrictionAction[] {
  return restrictionActions.filter((action: RestrictionAction) => {
    return (
      action.context === RESTRICTION_MODEL_CONTEXTS.CHANNEL_LEVEL &&
      action.createdAt &&
      action.otaId === null && // otaIds are null for minor channels
      Boolean(action.channelId) // channelIds are filled in minor channels
    );
  });
}

/**
 * Filter restriction actions that belong to HOTEL_LEVEL context and carry a
 * restriction type channel pricing can act on. Overbooking-only actions are
 * excluded here, since channel pricing has no overbooking concept (COA-4070).
 * @param restrictionsActions
 */
export function filterHotelLevelRestrictions(
  restrictionsActions: RestrictionAction[],
): RestrictionAction[] {
  return restrictionsActions.filter((action: RestrictionAction) => {
    return (
      action.context === RESTRICTION_MODEL_CONTEXTS.HOTEL_LEVEL &&
      action.createdAt &&
      action.values.some((value) =>
        CHANNEL_PRICING_HOTEL_RESTRICTION_TYPES.includes(value.type),
      )
    );
  });
}

/**
 * Returns the most recent restriction action from the passed array of restriction actions
 * @param restrictionsActions
 *
 * @returns The most recent restriction action, or undefined if none found
 */
function findMostRecentAction(
  restrictionsActions: RestrictionAction[],
): RestrictionAction {
  return restrictionsActions.reduce((mostRecent, current) => {
    if (!current.createdAt || !mostRecent.createdAt) {
      return mostRecent;
    }
    return current.createdAt.isAfter(mostRecent.createdAt)
      ? current
      : mostRecent;
  });
}

/**
 * Extracts unique OTA IDs from restriction actions.
 *
 * @param restrictionsActions
 * @returns Array of unique OTA IDs occurring in the restriction actions (duplicates removed)
 */
function getOtaIds(restrictionsActions: RestrictionAction[]): string[] {
  const result: string[] = [];

  restrictionsActions.forEach((restriction) => {
    if (restriction.otaId && !result.includes(restriction.otaId)) {
      result.push(restriction.otaId);
    }
  });

  return result;
}

/**
 * Extracts unique Channel IDs from restriction actions.
 *
 * @param restrictionsActions - Array of restriction actions
 * @returns Array of unique Channel IDs (duplicates removed)
 */
function getChannelIds(restrictionsActions: RestrictionAction[]): string[] {
  const result: string[] = [];

  restrictionsActions.forEach((restriction) => {
    if (restriction.channelId && !result.includes(restriction.channelId)) {
      result.push(restriction.channelId);
    }
  });

  return result;
}

/**
 * Groups restriction actions by a specific property into a map structure.
 *
 * Example: grouping by 'otaId' creates:
 * {
 *   'booking.com': [action1, action2],
 *   'expedia': [action3]
 * }
 *
 * @param key - The property to group by (must be a string property)
 * @param restrictionsActions - Array of restriction actions to group
 * @returns Object with keys being the property values and values being arrays of actions
 */
function groupRestrictionsByKey(
  key: keyof RestrictionAction,
  restrictionsActions: RestrictionAction[],
): Record<string, RestrictionAction[]> {
  const groups: Record<string, RestrictionAction[]> = {};

  for (const restriction of restrictionsActions) {
    const mapKey = restriction[key];

    // Only process string values
    if (typeof mapKey === 'string' && mapKey) {
      // Initialize array if first time seeing this key
      if (!groups[mapKey]) {
        groups[mapKey] = [];
      }
      groups[mapKey].push(restriction);
    }
  }

  return groups;
}

/**
 * Groups restriction actions by their OTA ID.
 * This is a specialized version of groupRestrictionsByKey for OTA IDs.
 *
 * @param restrictionActions - Array of restriction actions to group
 * @returns Object with OTA IDs as keys and arrays of actions as values
 */
function groupRestrictionsByOtaId(
  restrictionActions: RestrictionAction[],
): Record<string, RestrictionAction[]> {
  return groupRestrictionsByKey('otaId', restrictionActions);
}

/**
 * Groups restriction actions by their Channel ID.
 * This is a specialized version of groupRestrictionsByKey for Channel IDs.
 *
 * @param restrictionActions - Array of restriction actions to group
 * @returns Object with Channel IDs as keys and arrays of actions as values
 */
function groupRestrictionsByChannelId(
  restrictionActions: RestrictionAction[],
): Record<string, RestrictionAction[]> {
  return groupRestrictionsByKey('channelId', restrictionActions);
}

/**
 * Returns a map-like object of days containing an array of `otaId`s originated
 * from restrictions of CHANNEL_LEVEL with exceptions.
 *
 * @param periods
 */
export function latestChannelExceptionRestrictionActionsPerDayAndChannel(
  periods: Period[],
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const period of periods) {
    const dayId = period.day.id!;
    const channelLevelActions = filterChannelLevelRestrictions(
      period.restrictionActions,
    );

    if (channelLevelActions.length === 0) {
      continue;
    }

    // Group actions by OTA ID and find unique OTA IDs
    const otaIds = getOtaIds(channelLevelActions);
    const groupedActions = groupRestrictionsByOtaId(channelLevelActions);

    for (const otaId of otaIds) {
      const actionsForOta = groupedActions[otaId];
      if (!actionsForOta?.length) {
        continue;
      }

      // Find the most recent action for this OTA
      const latestAction = findMostRecentAction(actionsForOta);

      // Include this OTA if the latest action has an exception
      if (latestAction?.hasException) {
        if (!result[dayId]) {
          result[dayId] = [];
        }
        result[dayId].push(otaId);
      }
    }
  }

  return result;
}

/**
 * Finds minor channel restriction actions that have exceptions, grouped by day.
 *
 * @param periods - Array of periods containing restriction actions
 * @returns Object mapping day IDs to arrays of minor channel identifiers
 */
export function latestMinorChannelExceptionRestrictionActionsPerDayAndChannel(
  periods: Period[],
): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  for (const period of periods) {
    const dayId = period.day.id!;
    const minorChannelActions = filterMinorChannelLevelRestrictions(
      period.restrictionActions,
    );

    if (minorChannelActions.length === 0) {
      continue;
    }

    // Group actions by Channel ID and find unique Channel IDs
    const channelIds = getChannelIds(minorChannelActions);
    const groupedActions = groupRestrictionsByChannelId(minorChannelActions);

    for (const channelId of channelIds) {
      const actionsForChannel = groupedActions[channelId];
      if (!actionsForChannel?.length) {
        continue;
      }

      // Find the most recent action for this channel
      const latestAction = findMostRecentAction(actionsForChannel);

      // Include this channel if the latest action has an exception
      if (latestAction?.hasException) {
        if (!result[dayId]) {
          result[dayId] = [];
        }
        // Use generic minor channel ID (could use channelId for more granular info)
        result[dayId].push(RESTRICTION_MINOR_CHANNEL_ID);
      }
    }
  }

  return result;
}

/**
 * Identifies dates where the latest HOTEL_LEVEL restriction action includes an exception.
 *
 * @param periods - Array of periods, each containing day data and restriction actions.
 * @returns An array of dates where the latest HOTEL_LEVEL restriction action has an exception.
 */
export function latestHotelLevelRestrictionActionsPerDay(
  periods: Period[],
): string[] {
  const result: string[] = [];

  for (const period of periods) {
    const dayId = period.day.id!;

    const hotelLevelActionsForDay = filterHotelLevelRestrictions(
      period.restrictionActions,
    );

    if (hotelLevelActionsForDay.length === 0) {
      continue;
    }

    const latestAction: RestrictionAction = findMostRecentAction(
      hotelLevelActionsForDay,
    );

    if (latestAction?.hasException) {
      result.push(dayId);
    }
  }

  return result;
}
